import { Button, type DialogProps } from '@mui/material';
import { DateTime } from 'luxon';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useUpdateEvent } from '~/api/events/update';
import { Dialog, DialogTitle } from '~/components/dialogs/components';
import { DialogActions, DialogContent } from '~/components/dialogs/lib';
import type { EditEventDialogProps__Event as Event } from '../../queries/events.generated';
import { useEventFormik } from '../lib/event-formik';
import { EventForm, type EventMutationValues } from './event-form';

export interface EditEventDialogProps extends Omit<DialogProps, 'children'> {
  close: () => void;
  event: Event;
  open: boolean;
}

export const EditEventDialog = ({ close, event, open }: EditEventDialogProps) => {
  const { t } = useTranslation();
  const [updateEvent] = useUpdateEvent();

  const onSubmit = async (values: EventMutationValues) => {
    const patch = event.syncingFromAdmin
      ? {
          includeInPlayoutReporting: values.includeInPlayoutReporting,
          sponsorStartTime: values.sponsorStartTime,
          sponsorStopTime: values.sponsorStopTime,
        }
      : values;
    const { errors } = await updateEvent({
      variables: { id: event.id, patch },
    });
    if (!errors?.length) close();
  };

  const initialValues = useMemo(
    () => ({
      includeInPlayoutReporting: event.includeInPlayoutReporting,
      name: event.name,
      sponsorStartTime: event.sponsorStartTime ?? '',
      sponsorStopTime: event.sponsorStopTime ?? '',
      start: DateTime.fromISO(event.start),
      stop: DateTime.fromISO(event.stop),
    }),
    [event],
  );

  const formik = useEventFormik(initialValues, onSubmit);

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={open}
      onClose={close}
      TransitionProps={{ onExited: () => formik.resetForm() }}
    >
      <DialogTitle onClose={close}>
        {t('pages.event_types.events_tab.edit_event_dialog.title', { eventName: event.name })}
      </DialogTitle>

      <DialogContent sx={{ overflowX: 'clip' }}>
        <EventForm formik={formik} id="edit-event-form" synced={event.syncingFromAdmin} />
      </DialogContent>

      <DialogActions>
        <Button disabled={formik.isSubmitting} onClick={close} variant="outlined">
          {t('common.dialogs.cancel')}
        </Button>
        <Button
          color="primary"
          form="edit-event-form"
          disabled={formik.isSubmitting || !formik.isValid || !formik.dirty}
          type="submit"
          variant="contained"
        >
          {t('common.dialogs.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
